/**
 * Callback-inbox list (PR-6a — operational observability).
 *
 * Raw inbound provider callbacks indexed by plugin + dedup key. The
 * row state is server-derived (pending / processed / dead_lettered)
 * from ``processed_at`` + ``processing_error``. ``raw_body`` is
 * deliberately not surfaced at this listing — it requires step-up
 * auth, landing in PR-6b.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Inbox, RotateCw } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/shell/ListPagination";
import { useEnvironment } from "@/lib/environment-context";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { CopyId } from "@/components/shell/CopyId";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { formatMethod } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getCallbackInbox,
  type CallbackInboxRow,
  type CallbackInboxState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const STATES: readonly CallbackInboxState[] = [
  "pending",
  "processed",
  "dead_lettered",
] as const;

const STATE_VARIANT: Record<CallbackInboxState, BadgeProps["variant"]> = {
  pending: "info",
  processed: "success",
  dead_lettered: "destructive",
};

export function CallbackInboxListPage() {
  const [params, setParams] = useSearchParams();
  const { page, setPage } = usePageParam();
  const { environment: env } = useEnvironment();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = (params.get("state") as CallbackInboxState | null) ?? undefined;
  const plugin = params.get("plugin") ?? undefined;
  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      // Also skips the mount run, so a shared ?page=3 link keeps its page.
      if (searchInput === (params.get("q") ?? "")) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      next.delete("page");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["callback-inbox", { env, state, plugin, q, page }] as const,
    queryFn: () => getCallbackInbox({ environment: env, state, plugin, q, page, page_size: LIST_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load callback inbox", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state || plugin || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<CallbackInboxRow>>>(
    () => [
      {
        key: "id",
        header: "Inbox row",
        cell: (r) => (
          <CopyId id={r.id} />
        ),
      },
      {
        key: "plugin",
        header: "Plugin",
        cell: (r) => <span className="text-sm">{formatMethod(r.plugin)}</span>,
      },
      {
        key: "tenant",
        header: "Provider account",
        cell: (r) =>
          r.provider_account ? (
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm">{r.provider_account.name}</span>
              {r.provider_account.tenant.environment === "test" ? <Badge variant="muted">Test</Badge> : null}
            </div>
          ) : (
            <span className="text-xs italic text-muted-foreground">
              (unresolved)
            </span>
          ),
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge dot variant={STATE_VARIANT[r.state]} className="capitalize">
            {r.state.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        key: "attempts",
        header: "Attempts",
        className: "text-right tabular-nums",
        cell: (r) => <span className="text-sm">{r.attempts}</span>,
      },
      {
        key: "received_at",
        header: "Received",
        className: "text-muted-foreground",
        cell: (r) => (
          <RelativeTime iso={r.received_at} />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        icon={Inbox}
        title="Callback inbox"
        description="Raw inbound provider callbacks. The body itself is gated behind step-up auth (PR-6b)."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by dedup key…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        filters={
          <>
            <FilterGroup label="State">
              <FilterChip
                active={state === undefined}
                onClick={() => setFilter("state", null)}
              >
                All
              </FilterChip>
              {STATES.map((s) => (
                <FilterChip
                  key={s}
                  active={state === s}
                  onClick={() => setFilter("state", s)}
                >
                  {s.replace(/_/g, " ")}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<CallbackInboxRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/callback-inbox/${r.id}`)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Inbox}
              title="No tenants granted yet"
              description="Callbacks appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Inbox}
              title="No callbacks match these filters"
              description="Try clearing a filter. Callbacks flow in as providers post to your endpoints."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/callback-inbox">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />
    </>
  );
}

export default CallbackInboxListPage;
